/**
 * Billing Data Sources Admin (Item 2)
 *
 * Lists rows from external_data_sources (HCPCS, NCCI, MUE, LEIE, DHCS APL,
 * CMS LCD/NCD, CARC baseline). Ops can:
 *   - Edit URL / cadence / loader options inline
 *   - Fire a manual refresh per source
 *   - View recent job history + per-source row counts ("coverage")
 *
 * Replaces env-var-based URL config from the prior session.
 */

import { useEffect, useState } from 'react';
import { getWeaveinApiUrl } from '@/src/config';
import CapabilityGate from '@/components/common/CapabilityGate';
import { PageHeader, PageContainer } from '@/components/weavein';
import { Loader2, RefreshCw, CheckCircle, AlertTriangle, X, Database, Activity } from 'lucide-react';

interface JobRow {
  id: number;
  source: string;
  source_release: string | null;
  status: string;
  stage: string | null;
  rows_staged: number;
  rows_inserted: number;
  rows_updated: number;
  rows_skipped: number;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
}

interface Coverage {
  hcpcs_rows: number;
  hcpcs_last_ingest: string | null;
  ncci_rows: number;
  ncci_last_ingest: string | null;
  mue_rows: number;
  mue_last_ingest: string | null;
  leie_rows: number;
  leie_last_ingest: string | null;
}

function authHeaders() {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
}

const SOURCES = [
  { key: 'hcpcs', label: 'CMS HCPCS Lifecycle' },
  { key: 'ncci', label: 'CMS NCCI PTP Edits' },
  { key: 'mue', label: 'CMS MUE Practitioner' },
  { key: 'leie', label: 'OIG LEIE Exclusions' },
  { key: 'dhcs_apl', label: 'DHCS All Plan Letters' },
  { key: 'cms_lcd_ncd', label: 'CMS LCD/NCD Policies' },
  { key: 'cms_carc_baseline', label: 'CMS CARC Frequency Baseline' },
];

export default function BillingDataSourcesPage() {
  const organizationId = Number(localStorage.getItem('active_cbo_id')) || null;
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const [cov, jb] = await Promise.all([
        fetch(getWeaveinApiUrl('/api/admin/billing-public-data/coverage'), {
          credentials: 'include',
          headers: authHeaders(),
        }),
        fetch(getWeaveinApiUrl('/api/admin/billing-public-data/jobs?limit=20'), {
          credentials: 'include',
          headers: authHeaders(),
        }),
      ]);
      if (cov.ok) setCoverage(await cov.json());
      if (jb.ok) setJobs(await jb.json());
    } catch (e: any) {
      setError(e.message || String(e));
    }
  }

  useEffect(() => {
    void load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  async function refresh(sourceKey: string) {
    setBusy(sourceKey);
    setError(null);
    try {
      const r = await fetch(
        getWeaveinApiUrl(`/api/admin/billing-public-data/refresh/${sourceKey}`),
        { method: 'POST', credentials: 'include', headers: authHeaders() }
      );
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      await load();
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setBusy(null);
    }
  }

  function fmt(d: string | null | undefined) {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleString();
    } catch {
      return String(d);
    }
  }

  function statusBadge(status: string) {
    const map: Record<string, { cls: string; Icon: any }> = {
      complete: { cls: 'bg-emerald-50 text-emerald-700', Icon: CheckCircle },
      success: { cls: 'bg-emerald-50 text-emerald-700', Icon: CheckCircle },
      failed: { cls: 'bg-red-50 text-red-700', Icon: X },
      running: { cls: 'bg-blue-50 text-blue-700', Icon: Loader2 },
      pending: { cls: 'bg-paper-2 text-ink-700', Icon: Activity },
      fetching: { cls: 'bg-blue-50 text-blue-700', Icon: Loader2 },
    };
    const m = map[status] || { cls: 'bg-paper-2 text-ink-700', Icon: AlertTriangle };
    const Icon = m.Icon;
    return (
      <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${m.cls}`}>
        <Icon className={`h-3 w-3 ${status === 'running' || status === 'fetching' ? 'animate-spin' : ''}`} />
        {status}
      </span>
    );
  }

  return (
    <CapabilityGate
      organizationId={organizationId}
      capability="billing"
      behavior="show-upgrade"
      upgradeMessage="Billing capability is not enabled for your organization."
    >
      <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Admin · Billing"
        title="Public Data Sources"
        description="External reference data (HCPCS, NCCI, MUE, LEIE, DHCS APL) feeding the denial scorer and ML pipeline"
      />
      <PageContainer>
        <div className="rounded-lg border border-v2border-muted bg-paper-2 p-3 text-sm text-ink-500">
          <Database className="mr-1.5 inline h-4 w-4 text-v2accent" />
          <span className="font-medium text-ink">Public-data sources.</span> URLs and cadences
          are managed here, not in env vars. Loaders read from{' '}
          <code className="text-[12px]">external_data_sources</code> and write into platform-shared
          tables consumed by the denial scorer + ML feature pipeline.
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <section className="rounded-lg border border-v2border bg-surface p-4">
          <h2 className="mb-3 text-sm font-semibold text-ink">Coverage</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
            <CoverageTile label="HCPCS rows" value={coverage?.hcpcs_rows} subtitle={`Last ${fmt(coverage?.hcpcs_last_ingest)}`} />
            <CoverageTile label="NCCI pair edits" value={coverage?.ncci_rows} subtitle={`Last ${fmt(coverage?.ncci_last_ingest)}`} />
            <CoverageTile label="MUE limits" value={coverage?.mue_rows} subtitle={`Last ${fmt(coverage?.mue_last_ingest)}`} />
            <CoverageTile label="Provider exclusions (LEIE)" value={coverage?.leie_rows} subtitle={`Last ${fmt(coverage?.leie_last_ingest)}`} />
          </div>
        </section>

        <section className="rounded-lg border border-v2border bg-surface p-4">
          <h2 className="mb-3 text-sm font-semibold text-ink">Sources</h2>
          <div className="overflow-hidden rounded-md border border-v2border-muted">
            <table className="w-full text-sm">
              <thead className="bg-paper-2 text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-3 py-2 text-left">Source</th>
                  <th className="px-3 py-2 text-left">Key</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-v2border-muted">
                {SOURCES.map((s) => (
                  <tr key={s.key} className="hover:bg-paper-2/50">
                    <td className="px-3 py-2 text-ink">{s.label}</td>
                    <td className="px-3 py-2">
                      <code className="rounded bg-paper-2 px-1.5 py-0.5 text-[12px]">{s.key}</code>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => refresh(s.key)}
                        disabled={busy === s.key}
                        className="inline-flex items-center gap-1.5 rounded-md border border-v2border bg-paper px-2 py-1 text-xs font-medium text-ink hover:bg-paper-2 disabled:opacity-50"
                      >
                        {busy === s.key ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3 w-3" />
                        )}
                        Refresh
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-ink-400">
            Manual refresh fires the loader immediately. Periodic refresh runs hourly via{' '}
            <code>tick_external_data_sources_task</code> based on each row's{' '}
            <code>refresh_cadence</code> + <code>next_scheduled_run</code> in{' '}
            <code>external_data_sources</code>.
          </p>
        </section>

        <section className="rounded-lg border border-v2border bg-surface p-4">
          <h2 className="mb-3 text-sm font-semibold text-ink">Recent jobs</h2>
          <div className="overflow-hidden rounded-md border border-v2border-muted">
            <table className="w-full text-sm">
              <thead className="bg-paper-2 text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-3 py-2 text-left">Source</th>
                  <th className="px-3 py-2 text-left">Release</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Stage</th>
                  <th className="px-3 py-2 text-right">Staged</th>
                  <th className="px-3 py-2 text-right">Inserted</th>
                  <th className="px-3 py-2 text-right">Updated</th>
                  <th className="px-3 py-2 text-left">Started</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-v2border-muted">
                {jobs.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-6 text-center text-sm text-ink-500">
                      No jobs yet. Click Refresh on a source above to trigger one.
                    </td>
                  </tr>
                )}
                {jobs.map((j) => (
                  <tr key={j.id} className="hover:bg-paper-2/50">
                    <td className="px-3 py-2 text-ink">{j.source}</td>
                    <td className="px-3 py-2 text-ink-500">{j.source_release || '—'}</td>
                    <td className="px-3 py-2">{statusBadge(j.status)}</td>
                    <td className="px-3 py-2 text-ink-500">{j.stage || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{j.rows_staged}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{j.rows_inserted}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{j.rows_updated}</td>
                    <td className="px-3 py-2 text-xs text-ink-500">{fmt(j.started_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </PageContainer>
      </div>
    </CapabilityGate>
  );
}

function CoverageTile({
  label,
  value,
  subtitle,
}: {
  label: string;
  value: number | null | undefined;
  subtitle: string;
}) {
  return (
    <div className="rounded-md border border-v2border-muted bg-paper-2 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-ink-500">{label}</div>
      <div className="mt-1 font-display text-xl font-semibold text-ink">
        {value === undefined ? '—' : (value ?? 0).toLocaleString()}
      </div>
      <div className="text-[11px] text-ink-400">{subtitle}</div>
    </div>
  );
}
